// ✅ src/pages/Inventory/PurchaseStoreMaterialForm.jsx
// ✅ UPDATED:
// - supports localStorage.branch = "HO" or "HEAD-OFFICE"
// - fixes wrong branch type detection
// - keeps commonHeaders and existing API flow
// - modern design (glass cards, Lucide icons, dark mode)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import {
  ArrowLeft,
  Plus,
  Save,
  Search,
  CheckCircle,
  Trash2,
  ClipboardList,
  Upload,
  Building2,
} from "lucide-react";

const API = "https://nissi-india.com/api";
const todayISO = () => new Date().toISOString().split("T")[0];

const PurchaseStoreMaterialAdd = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const requestid = sp.get("reqId");
  const isEdit = Boolean(requestid);

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const loginuserid = Number(userId) || 37;
  const fld_branchid = Number(branchId) || 8;

  // ✅ normalize localStorage branch value
  const normalizedBranch = String(branch || "").trim().toUpperCase();
  const isHO =
    normalizedBranch === "HO" ||
    normalizedBranch === "HEAD-OFFICE" ||
    normalizedBranch === "HEAD OFFICE";

  const storedBranch = branch || "HO";
  const initialBranchType = isHO ? "HO" : "BRANCH";

  const [branchType, setBranchType] = useState(initialBranchType);
  const [location, setLocation] = useState(initialBranchType);

  const [metaLoaded, setMetaLoaded] = useState(false);

  /* =========================
     MASTER FIELDS
  ========================= */
  const [purchaseRequestNo, setPurchaseRequestNo] = useState("");
  const [date, setDate] = useState(todayISO());
  const [type, setType] = useState("H");

  const [storeOptions, setStoreOptions] = useState([]);
  const [store, setStore] = useState("");
  const [toStore] = useState("HEAD-OFFICE");

  const [approvedPersons, setApprovedPersons] = useState([]);
  const [reqApprove, setReqApprove] = useState("");

  const [projectCodes, setProjectCodes] = useState([]);
  const [projectCode, setProjectCode] = useState("");

  const [departments, setDepartments] = useState([]);
  const [department, setDepartment] = useState("");

  const [employees, setEmployees] = useState([]);
  const [employee, setEmployee] = useState("");

  const [requestedFor, setRequestedFor] = useState("");
  const [remarks, setRemarks] = useState("");

  const [authorizedPersons, setAuthorizedPersons] = useState([]);
  const [authorizedPerson, setAuthorizedPerson] = useState("");

  /* =========================
     FILTER AREA
  ========================= */
  const [itemCategories, setItemCategories] = useState([]);
  const [itemCategory, setItemCategory] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");

  /* =========================
     TABLE 1: SEARCH RESULTS
  ========================= */
  const [searchResults, setSearchResults] = useState([]);
  const [searchSelectedIds, setSearchSelectedIds] = useState(() => new Set());

  /* =========================
     TABLE 2: ADDED ITEMS
  ========================= */
  const [addedItems, setAddedItems] = useState([]);
  const [submitOk, setSubmitOk] = useState(false);

  /* =========================
     DOCS QUEUE
  ========================= */
  const [docFiles, setDocFiles] = useState([]);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  // ✅ keep state synced if branch changes
  useEffect(() => {
    if (isHO) {
      setBranchType("HO");
      setLocation("HO");
    } else {
      setBranchType("BRANCH");
      setLocation("BRANCH");
    }
  }, [isHO]);

  /* =========================
     LOAD DROPDOWNS (META)
  ========================= */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setMetaLoaded(false);

        const [
          projectRes,
          storeRes,
          deptRes,
          empRes,
          authRes,
          apprRes,
          catRes,
        ] = await Promise.all([
          axios.get(
            `${API}/getProjectDropdown?branchtype=${location}&branchid=${fld_branchid}`,
            { headers: commonHeaders }
          ),
          axios.get(`${API}/getStoresByBranchId?branchid=${fld_branchid}`, {
            headers: commonHeaders,
          }),
          axios.get(`${API}/pr_getDepartmentsByBranch?branchid=${fld_branchid}`, {
            headers: commonHeaders,
          }),
          axios.get(`${API}/pr_getEmployeesByBranch?branchid=${fld_branchid}`, {
            headers: commonHeaders,
          }),
          axios.get(`${API}/pr_getAuthorizedPersons`, {
            headers: commonHeaders,
          }),
          axios.get(`${API}/pr_getApprovedPersons`, {
            headers: commonHeaders,
          }),
          axios.get(`${API}/branchItems_cat`, {
            headers: commonHeaders,
          }),
        ]);

        if (!alive) return;

        setProjectCodes(projectRes.data.data || []);
        setStoreOptions(storeRes.data.data || []);
        setDepartments(deptRes.data.data || []);
        setEmployees(empRes.data.data || []);
        setAuthorizedPersons(authRes.data.data || []);
        setApprovedPersons(apprRes.data.data || []);
        setItemCategories(catRes.data.cat || []);

        setMetaLoaded(true);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load dropdowns");
      }
    })();

    return () => {
      alive = false;
    };
  }, [location, fld_branchid, commonHeaders]);

  /* =========================
     ADD MODE: PR NUMBER
  ========================= */
  useEffect(() => {
    if (isEdit) return;

    (async () => {
      try {
        const res = await axios.get(
          `${API}/generatePurchaseRequestNo?loginuserid=${loginuserid}`,
          { headers: commonHeaders }
        );
        setPurchaseRequestNo(res.data?.purchaseRequestNo || "AUTO");
      } catch {
        toast.error("Failed to generate request number");
      }
    })();
  }, [isEdit, loginuserid, commonHeaders]);

  /* =========================
     EDIT MODE: LOAD DETAILS
  ========================= */
  useEffect(() => {
    if (!isEdit) return;
    if (!metaLoaded) return;

    let alive = true;

    (async () => {
      try {
        const res = await axios.get(
          `${API}/purchaseRequest_viewDetails?requestid=${requestid}`,
          { headers: commonHeaders }
        );

        if (!alive) return;

        const payload = res.data?.data;
        if (!payload) {
          toast.error("No data returned for this request");
          return;
        }

        const { masterDetails, items, documents } = payload;

        if (!masterDetails) {
          toast.error("Master details not found");
          return;
        }

        setPurchaseRequestNo(masterDetails.fld_PurchaseRequestNo || "");

        const d = new Date(masterDetails.fld_Date);
        setDate(!isNaN(d) ? d.toISOString().split("T")[0] : todayISO());

        setType(masterDetails.fld_Type || "H");
        setStore(String(masterDetails.fld_Storeid || ""));
        setProjectCode(String(masterDetails.fld_projectid || ""));
        setDepartment(String(masterDetails.fld_deptid || ""));
        setEmployee(String(masterDetails.fld_Employeeid || ""));
        setRequestedFor(masterDetails.fld_Indentref || "");
        setRemarks(masterDetails.fld_Remarks || "");
        setReqApprove(String(masterDetails.fld_reqapprove || ""));
        setAuthorizedPerson(String(masterDetails.fld_autherized || ""));

        const mapped = (items || []).map((r) => ({
          itemid: r.itemid ?? r.fld_itemid,
          itemcode: r.itemcode,
          itemname: r.itemname,
          itemPartNo: r.itemPartNo,
          itemuom: r.itemuom,
          AvailableStock: r.AvailableStock ?? r.fld_closing_stock ?? 0,
          fld_qty: String(r.fld_qty ?? ""),
          fld_duedate: (() => {
            const dd = new Date(r.fld_duedate);
            return !isNaN(dd) ? dd.toISOString().split("T")[0] : "";
          })(),
          fld_life: String(r.fld_life ?? ""),
          fld_remarks: r.fld_remarks ?? "",
          fld_specification: r.fld_specification ?? "",
          isSubmit: true,
        }));

        setAddedItems(mapped);
        setSubmitOk(true);

        if ((documents || []).length > 0) {
          toast.info(`Existing documents: ${documents.length}`);
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load request details");
      }
    })();

    return () => {
      alive = false;
    };
  }, [isEdit, requestid, metaLoaded, commonHeaders]);

  /* =========================
     SEARCH
  ========================= */
  const handleSearch = async () => {
    if (!store) return toast.error("Please Select Store");
    if (!itemCategory || itemCategory === "Select") {
      return toast.error("Please Select Item Category");
    }

    try {
      const res = await axios.get(
        `${API}/pr_searchItemList?itemcategory=${encodeURIComponent(
          itemCategory
        )}&itemtype=Select&itemcode=${encodeURIComponent(
          itemCode
        )}&itemname=${encodeURIComponent(
          itemName
        )}&branchid=${fld_branchid}&storeid=${store}&purtype=${
          branchType === "HO" ? "H" : "B"
        }`,
        { headers: commonHeaders }
      );

      if (res.data.success) {
        setSearchResults(res.data.data || []);
        setSearchSelectedIds(new Set());
      } else {
        setSearchResults([]);
        toast.error(res.data.message || "No items found");
      }
    } catch {
      toast.error("Failed to fetch items");
    }
  };

  const toggleSearchSelect = (itemid) => {
    setSearchSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemid)) next.delete(itemid);
      else next.add(itemid);
      return next;
    });
  };

  /* =========================
     ADD BUTTON
  ========================= */
  const handleAdd = () => {
    if (searchSelectedIds.size === 0) {
      return toast.warning("Select items to add");
    }

    const selectedRows = searchResults.filter((r) =>
      searchSelectedIds.has(r.itemid)
    );

    setAddedItems((prev) => {
      const map = new Map(prev.map((p) => [p.itemid, p]));
      for (const r of selectedRows) {
        if (!map.has(r.itemid)) {
          map.set(r.itemid, {
            itemid: r.itemid,
            itemcode: r.itemcode,
            itemname: r.itemname,
            itemPartNo: r.itemPartNo,
            itemuom: r.itemuom,
            AvailableStock: r.AvailableStock ?? r.fld_closing_stock ?? 0,
            fld_qty: "",
            fld_duedate: "",
            fld_life: "",
            fld_remarks: "",
            fld_specification: "",
            isSubmit: false,
          });
        }
      }
      return Array.from(map.values());
    });

    setSearchSelectedIds(new Set());
    setSubmitOk(false);
    toast.success("Added. Fill Qty/DueDate and submit.");
  };

  const updateAddedItem = (itemid, patch) => {
    setAddedItems((prev) =>
      prev.map((r) => (r.itemid === itemid ? { ...r, ...patch } : r))
    );
    setSubmitOk(false);
  };

  const removeAddedItem = (itemid) => {
    setAddedItems((prev) => prev.filter((r) => r.itemid !== itemid));
    setSubmitOk(false);
  };

  /* =========================
     SUBMIT ITEMS
  ========================= */
  const handleSubmitItems = () => {
    if (addedItems.length === 0) return toast.error("No added items");

    const reqDate = new Date(date);
    if (isNaN(reqDate)) return toast.error("Please select Request Date");

    const rowsToSubmit = addedItems.filter((r) => r.isSubmit);
    if (rowsToSubmit.length === 0) {
      return toast.warning("Tick Submit checkbox for at least one item");
    }

    for (const r of rowsToSubmit) {
      const qty = Number(r.fld_qty);
      if (!qty || qty <= 0) {
        return toast.error(`Enter valid Qty for ${r.itemcode}`);
      }
      if (!r.fld_duedate) {
        return toast.error(`Select DueDate for ${r.itemcode}`);
      }

      const due = new Date(r.fld_duedate);
      if (isNaN(due)) return toast.error(`Invalid DueDate for ${r.itemcode}`);
      if (due < reqDate) {
        return toast.error(`DueDate must be >= Request Date for ${r.itemcode}`);
      }
    }

    toast.success("Items submitted successfully");
    setSubmitOk(true);
  };

  /* =========================
     DOC QUEUE
  ========================= */
  const onPickDocs = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setDocFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeDoc = (idx) =>
    setDocFiles((prev) => prev.filter((_, i) => i !== idx));

  /* =========================
     SAVE
  ========================= */
  const handleSave = async () => {
    if (!submitOk) return toast.error("Please click Submit Items first");

    if (!purchaseRequestNo) return toast.error("Purchase Req Number missing");
    if (!date) return toast.error("Date is required");
    if (!store) return toast.error("Store is required");
    if (!department || department === "Select") {
      return toast.error("Department is required");
    }
    if (!reqApprove || reqApprove === "Select") {
      return toast.error("Req Approve is required");
    }
    if (!requestedFor) return toast.error("Requested For is required");

    const finalItems = addedItems
      .filter((r) => r.isSubmit)
      .map((r) => ({
        itemid: r.itemid,
        fld_qty: r.fld_qty,
        fld_duedate: r.fld_duedate,
        fld_life: r.fld_life || "",
        fld_remarks: r.fld_remarks || "",
        fld_specification: r.fld_specification || "",
      }));

    if (finalItems.length === 0) {
      return toast.error("No submitted items to save");
    }

    const fd = new FormData();

    if (isEdit) fd.append("PurchaseRequestid", requestid);

    fd.append("fld_PurchaseRequestNo", purchaseRequestNo);
    fd.append("fld_Date", date);
    fd.append("fld_Type", type);
    fd.append("fld_Storeid", store);
    fd.append("fld_Projectid", projectCode || "");
    fd.append("fld_Deptid", department);
    fd.append("fld_Employeeid", employee || "");
    fd.append("fld_Indentref", requestedFor);
    fd.append("fld_Remarks", remarks || "");
    fd.append("fld_userid", String(loginuserid));
    fd.append("fld_branchid", String(fld_branchid));
    fd.append("fld_toStoreid", "1");
fd.append("fld_priorpurchaseexistforall", "0");
    fd.append("autherized", authorizedPerson || "");
    fd.append("reqapprove", reqApprove || "");
    fd.append("loginuserid", String(loginuserid));
    fd.append("replaceDocs", "true");
    fd.append("items", JSON.stringify(finalItems));

    for (const f of docFiles) {
      fd.append("files", f);
    }

    try {
      const res = await axios.post(`${API}/purchaseReq_saveAll`, fd, {
        headers: {
          ...commonHeaders,
          Accept: "application/json",
        },
      });

if (res.data?.success) {
  toast.success(res.data.message || "Saved successfully");
  navigate("/inventory/Purchase_Store_Material_Req");   // ← go to list page
}else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      const data = err?.response?.data;

      if (err?.response?.status === 409) {
        toast.error(data?.message || "This data is already inserted.");
        return;
      }

      toast.error(data?.message || "Save failed");
    }
  };

  const handleBranchTypeChange = (value) => {
    setBranchType(value);
    setLocation(value === "HO" ? "HO" : "BRANCH");
    setSubmitOk(false);
  };

  const modeTitle = isEdit
    ? "Purchase / Material Requisition Edit"
    : "Purchase / Material Requisition Add";

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">{modeTitle}</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header + Back */}
        <div
          className={`p-6 rounded-2xl shadow-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <ClipboardList className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{modeTitle}</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {isEdit ? "Update material requisition details" : "Create a new material requisition"}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
              isDark
                ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                : "border-slate-300 text-slate-700 hover:bg-slate-100"
            }`}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* Main Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Request Details</h3>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Branch Type */}
            <div>
              <label className={labelClass}>Branch Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleBranchTypeChange("HO")}
                  disabled={!isHO}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                    branchType === "HO"
                      ? `${buttonClass} ${hoverClass} text-white shadow`
                      : isDark
                      ? "bg-slate-800 text-slate-300 border border-slate-600"
                      : "bg-gray-100 text-gray-700 border border-gray-200"
                  } ${!isHO ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Building2 size={16} />
                  H.O
                </button>

                <button
                  type="button"
                  onClick={() => handleBranchTypeChange("BRANCH")}
                  disabled={isHO}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition ${
                    branchType === "BRANCH"
                      ? "bg-blue-600 text-white shadow"
                      : isDark
                      ? "bg-slate-800 text-slate-300 border border-slate-600"
                      : "bg-gray-100 text-gray-700 border border-gray-200"
                  } ${isHO ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Branch
                </button>
              </div>
            </div>

            {/* PR Number */}
            <div>
              <label className={labelClass}>Purchase Req Number</label>
              <div className={`text-lg font-bold mt-1 text-${baseColor}-600`}>
                {purchaseRequestNo || "—"}
              </div>
            </div>

            {/* Master Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              {/* Left Column */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    className={inputClass}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelClass}>Type</label>
                  <input
                    type="text"
                    className={`${inputClass} opacity-70 cursor-not-allowed`}
                    value={type}
                    disabled
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Store <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={store}
                    onChange={(e) => setStore(e.target.value)}
                  >
                    <option value="">Select</option>
                    {storeOptions.map((s) => (
                      <option key={s.fld_storeid} value={s.fld_storeid}>
                        {s.fld_storename}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>To Store</label>
                  <input
                    type="text"
                    className={`${inputClass} opacity-70 cursor-not-allowed`}
                    value={toStore}
                    disabled
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    Req Approve <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={reqApprove}
                    onChange={(e) => setReqApprove(e.target.value)}
                  >
                    <option value="">Select</option>
                    {approvedPersons.map((p, i) => (
                      <option key={i} value={p.fld_setupid}>
                        {p.fld_username}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Project Code</label>
                  <select
                    className={inputClass}
                    value={projectCode}
                    onChange={(e) => setProjectCode(e.target.value)}
                  >
                    <option value="">Select</option>
                    {projectCodes.map((c, i) => (
                      <option key={i} value={c.fld_contractid}>
                        {c.fld_contractname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                  >
                    <option value="">Select</option>
                    {departments.map((d, i) => (
                      <option key={i} value={d.fld_deptid}>
                        {d.fld_departmentname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Employee</label>
                  <select
                    className={inputClass}
                    value={employee}
                    onChange={(e) => setEmployee(e.target.value)}
                  >
                    <option value="">Select</option>
                    {employees.map((emp) => (
  <option key={emp.employeeid} value={emp.employeeid}>
    {emp.employeename}
  </option>
))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    Requested For <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={requestedFor}
                    onChange={(e) => setRequestedFor(e.target.value)}
                    placeholder="Requested for..."
                  />
                </div>

                <div>
                  <label className={labelClass}>Remarks</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional remarks"
                  />
                </div>

                <div>
                  <label className={labelClass}>Authorized Person</label>
                  <select
                    className={inputClass}
                    value={authorizedPerson}
                    onChange={(e) => setAuthorizedPerson(e.target.value)}
                  >
                    <option value="">Select</option>
                    {authorizedPersons.map((u, i) => (
                      <option key={i} value={u.fld_setupid}>
                        {u.fld_username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filteration Area Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Filteration Area</h3>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
              <div>
                <label className={labelClass}>Item Category</label>
                <select
                  className={inputClass}
                  value={itemCategory}
                  onChange={(e) => setItemCategory(e.target.value)}
                >
                  <option value="">Select</option>
                  {itemCategories.map((cat, idx) => (
                    <option key={idx} value={cat.fld_itemcategoryid}>
                      {cat.fld_itemcategory_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Item Code</label>
                <input
                  className={inputClass}
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value)}
                  placeholder="Item code"
                />
              </div>

              <div>
                <label className={labelClass}>Item Name</label>
                <input
                  className={inputClass}
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="Item name"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
                onClick={handleSearch}
              >
                <Search size={16} />
                Search
              </button>

              <button
                type="button"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl shadow transition"
                onClick={handleAdd}
              >
                <Plus size={16} />
                Add Selected
              </button>
            </div>
          </div>
        </div>

        {/* Search Results Table */}
        {searchResults.length > 0 && (
          <div
            className={`rounded-2xl shadow-xl border overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
                : `bg-white ${currentSkin.glass} border-gray-200`
            }`}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">Search Results</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {searchResults.length} items
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">Part No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">UOM</th>
                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                  {searchResults.map((item, idx) => (
                    <tr
                      key={item.itemid}
                      className={`transition ${
                        isDark
                          ? "bg-gray-800/20 hover:bg-gray-700/30"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {item.itemcode}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{item.itemname}</td>
                      <td className="px-4 py-3">{item.itemPartNo}</td>
                      <td className="px-4 py-3">{item.itemuom}</td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={searchSelectedIds.has(item.itemid)}
                          onChange={() => toggleSearchSelect(item.itemid)}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Added Items Table */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Added Items</h3>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                }`}
              >
                {addedItems.length} items
              </span>
            </div>
          </div>

          {addedItems.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              No items added yet. Search and add items above.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Item Name</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">UOM</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Available</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Qty*</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">DueDate*</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Life</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Remarks</th>
                      <th className="px-3 py-3 text-left text-xs font-bold uppercase">Specification</th>
                      <th className="px-3 py-3 text-center text-xs font-bold uppercase">Submit</th>
                      <th className="px-3 py-3 text-center text-xs font-bold uppercase">Remove</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                    {addedItems.map((r) => (
                      <tr
                        key={r.itemid}
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-3 py-3 font-medium">{r.itemcode}</td>
                        <td className="px-3 py-3">{r.itemname}</td>
                        <td className="px-3 py-3">{r.itemuom}</td>
                        <td className="px-3 py-3">{r.AvailableStock}</td>
                        <td className="px-3 py-3">
                          <input
                            className={`w-20 px-2 py-1 rounded border text-sm ${
                              isDark
                                ? "bg-slate-800 border-slate-600 text-white"
                                : "bg-white border-slate-300"
                            }`}
                            value={r.fld_qty}
                            onChange={(e) =>
                              updateAddedItem(r.itemid, { fld_qty: e.target.value })
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            type="date"
                            className={`px-2 py-1 rounded border text-sm ${
                              isDark
                                ? "bg-slate-800 border-slate-600 text-white"
                                : "bg-white border-slate-300"
                            }`}
                            value={r.fld_duedate}
                            onChange={(e) =>
                              updateAddedItem(r.itemid, { fld_duedate: e.target.value })
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            className={`w-20 px-2 py-1 rounded border text-sm ${
                              isDark
                                ? "bg-slate-800 border-slate-600 text-white"
                                : "bg-white border-slate-300"
                            }`}
                            value={r.fld_life}
                            onChange={(e) =>
                              updateAddedItem(r.itemid, { fld_life: e.target.value })
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            className={`w-36 px-2 py-1 rounded border text-sm ${
                              isDark
                                ? "bg-slate-800 border-slate-600 text-white"
                                : "bg-white border-slate-300"
                            }`}
                            value={r.fld_remarks}
                            onChange={(e) =>
                              updateAddedItem(r.itemid, { fld_remarks: e.target.value })
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            className={`w-40 px-2 py-1 rounded border text-sm ${
                              isDark
                                ? "bg-slate-800 border-slate-600 text-white"
                                : "bg-white border-slate-300"
                            }`}
                            value={r.fld_specification}
                            onChange={(e) =>
                              updateAddedItem(r.itemid, {
                                fld_specification: e.target.value,
                              })
                            }
                          />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!r.isSubmit}
                            onChange={(e) =>
                              updateAddedItem(r.itemid, { isSubmit: e.target.checked })
                            }
                            className="w-4 h-4 rounded"
                          />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button
                            type="button"
                            className="p-1.5 rounded-full text-red-600 hover:bg-red-100 transition"
                            onClick={() => removeAddedItem(r.itemid)}
                            title="Remove"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleSubmitItems}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl shadow transition"
                >
                  <CheckCircle size={16} />
                  Submit Items
                </button>

                {submitOk && (
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-1">
                    <CheckCircle size={16} />
                    Submitted
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        {/* Documents Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <h3 className="text-xl font-bold">Documents (optional)</h3>
            </div>
          </div>

          <div className="p-6">
            <div
              className={`relative border-2 border-dashed rounded-xl p-6 transition ${
                isDark
                  ? "border-slate-600 hover:border-slate-500"
                  : "border-slate-300 hover:border-slate-400"
              }`}
            >
              <input
                type="file"
                multiple
                onChange={onPickDocs}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="flex flex-col items-center gap-2 text-center">
                <Upload size={28} className={isDark ? "text-slate-400" : "text-slate-500"} />
                <span className={`text-sm ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  Click or drag files to upload
                </span>
              </div>
            </div>

            {docFiles.length > 0 && (
              <div className="mt-4">
                <div className="font-semibold mb-2 text-sm">Selected Files:</div>
                <ul className="space-y-2">
                  {docFiles.map((f, idx) => (
                    <li
                      key={idx}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${
                        isDark ? "bg-slate-800/50" : "bg-gray-50"
                      }`}
                    >
                      <span>{f.name}</span>
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-700 text-xs font-medium"
                        onClick={() => removeDoc(idx)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="button"
            className={`inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
            onClick={handleSave}
          >
            <Save size={18} />
            Save Requisition
          </button>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default PurchaseStoreMaterialAdd;